import React, { useEffect, useState, useRef } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Typography,
  useTheme,
} from "@mui/material";

// List of languages to support
const LANGUAGES = [
  { code: "en", label: "English", flag: "🇺🇸", countryCode: "us" },
  { code: "es", label: "Spanish", flag: "🇪🇸", countryCode: "es" },
  { code: "fr", label: "French", flag: "🇫🇷", countryCode: "fr" },
  { code: "de", label: "German", flag: "🇩🇪", countryCode: "de" },
  { code: "zh-CN", label: "Chinese", flag: "🇨🇳", countryCode: "cn" },
  { code: "ja", label: "Japanese", flag: "🇯🇵", countryCode: "jp" },
  { code: "ko", label: "Korean", flag: "🇰🇷", countryCode: "kr" },
  { code: "ru", label: "Russian", flag: "🇷🇺", countryCode: "ru" },
  { code: "pt", label: "Portuguese", flag: "🇵🇹", countryCode: "pt" },
  { code: "it", label: "Italian", flag: "🇮🇹", countryCode: "it" },
  { code: "ar", label: "Arabic", flag: "🇸🇦", countryCode: "sa" },
  { code: "hi", label: "Hindi", flag: "🇮🇳", countryCode: "in" },
  { code: "tr", label: "Turkish", flag: "🇹🇷", countryCode: "tr" },
  { code: "vi", label: "Vietnamese", flag: "🇻🇳", countryCode: "vn" },
  { code: "id", label: "Indonesian", flag: "🇮🇩", countryCode: "id" },
  { code: "ne", label: "Nepali", flag: "🇳🇵", countryCode: "np" },
];

const GoogleTranslateBox = ({ fullWidth = false }) => {
  const theme = useTheme();
  const [selectedLang, setSelectedLang] = useState("en");
  const [, setIsReady] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    // 1. Check/Set Cookie on Load
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(";").shift();
    };
    const currentCookie = getCookie("googtrans");
    if (currentCookie) {
      const langCode = currentCookie.split("/")[2];
      if (langCode) setSelectedLang(langCode);
    }

    // 2. Inject Styles to Hide Google Widget
    const style = document.createElement("style");
    style.id = "google-translate-hiding-style";
    style.innerHTML = `
      .goog-te-banner-frame { display: none !important; }
      .goog-te-combo { display: none !important; }
      .skiptranslate { display: none !important; }
      #google_translate_element { display: none !important; } 
      body { top: 0 !important; }
    `;
    if (!document.getElementById("google-translate-hiding-style")) {
      document.head.appendChild(style);
    }

    // 3. Define the Global Callback
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: LANGUAGES.map((l) => l.code).join(","),
          layout:
            window.google.translate.TranslateElement.InlineLayout.VERTICAL,
          autoDisplay: false,
        },
        "google_translate_element"
      );
    };

    // 4. Inject the Script if not present
    if (!document.querySelector('script[src*="translate.google.com"]')) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    }

    // 5. Observer to Detect when the <select> is actually added to the DOM
    const targetNode = document.getElementById("google_translate_element");
    if (targetNode) {
      observerRef.current = new MutationObserver(() => {
        const select = targetNode.querySelector("select.goog-te-combo");
        if (select) {
          setIsReady(true);
          observerRef.current?.disconnect(); // Stop observing once found
        }
      });
      observerRef.current.observe(targetNode, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    setSelectedLang(newLang);

    // 1. Update the hidden Google Select
    const googleSelect = document.querySelector(".goog-te-combo");
    if (googleSelect) {
      googleSelect.value = newLang;
      googleSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // 2. Manually set the cookie to ensure persistence without reload issues
    document.cookie = `googtrans=/auto/${newLang}; path=/; domain=${window.location.hostname}`;
    document.cookie = `googtrans=/auto/${newLang}; path=/;`; // Fallback for localhost
  };

  return (
    <FormControl variant="standard" sx={{ m: 1, width: fullWidth ? '100%' : 140 }}>
      <Select
        value={selectedLang}
        onChange={handleLanguageChange}
        disableUnderline
        sx={{
          color: "text.primary",
          fontSize: "0.875rem",
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            justifyContent: 'center',
            gap: 1,
            paddingTop: "4px",
            paddingBottom: "4px",
            textAlign: 'center',
            minWidth: '100%',
          },
          "& .MuiSvgIcon-root": {
            color: "text.primary",
            right: 0,
          },
          width: '100%',
        }}
        renderValue={(selected) => {
          const lang = LANGUAGES.find((l) => l.code === selected);
          if (!lang) return selected;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", width: '100%', justifyContent: fullWidth ? 'center' : 'flex-start' }}>
              <img
                src={`https://flagcdn.com/w20/${lang.countryCode}.png`}
                alt={lang.label}
                style={{ width: "20px", height: "auto", borderRadius: "2px" }}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {lang.label}
              </Typography>
            </div>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              bgcolor: "rgba(18, 18, 18, 0.95)", // Match dark theme
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.1)",
              "& .MuiMenuItem-root": {
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.1)",
                },
                "&.Mui-selected": {
                  bgcolor: "rgba(99, 102, 241, 0.2)", // Primary color hint
                  "&:hover": {
                    bgcolor: "rgba(99, 102, 241, 0.3)",
                  },
                },
              },
            },
          },
        }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            value={lang.code}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1,
            }}
          >
            <img
              src={`https://flagcdn.com/w20/${lang.countryCode}.png`}
              alt={lang.label}
              style={{ width: "20px", height: "auto", borderRadius: "2px" }}
            />
            <Typography variant="body2" noWrap>
              {lang.label}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default GoogleTranslateBox;
